import type {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponseHeaders,
} from 'axios';

interface IResponseError<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
> {
  data: D;
  headers: H;
  status: Nullish<number>;
  statusText: Nullish<string>;
  config?: AxiosRequestConfig<D>;
}

type ResponseErrorConstructorParamters<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
> = IResponseError<D, H> & Error;

abstract class ResponseError<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
>
  extends Error
  implements IResponseError<D, H>
{
  name: string;
  message!: string;

  data: D;
  headers: H;
  status: Nullish<number>;
  statusText: Nullish<string>;
  config?: AxiosRequestConfig<D>;

  constructor({
    data,
    headers,
    message,
    name,
    status,
    statusText,
    config,
  }: ResponseErrorConstructorParamters<D, H>) {
    super(message);
    this.data = data;
    this.headers = headers;
    this.name = name ?? message;
    this.status = status;
    this.statusText = statusText;
    this.message = message;
    this.config = config;
  }
}

export class ApiError<
  D extends {},
  H extends AxiosRequestHeaders | AxiosResponseHeaders,
> extends ResponseError<D, H> {
  constructor(params: ResponseErrorConstructorParamters<D, H>) {
    super({ ...params });
  }
}

export const BASE_ERROR_CODE = {
  '9999': '9999',
  '401': '401',

  '3401': '3401',
} as const;

export const BASE_ERROR_CODE_NAME_MAPPER = Object.freeze({
  [BASE_ERROR_CODE[9999]]: 'UnknownError',
  [BASE_ERROR_CODE[401]]: 'UnauthorizedError',

  [BASE_ERROR_CODE[3401]]: 'ValidateTokenFailed',
} as const);
